import { useMemo, useState } from 'react';
import { Modal } from '../Modal';
import { Combobox } from '../Combobox';
import { useChangeExecutor } from '../../api/mutations';
import { useAccounts } from '../../api/hooks';
import { btnPrimary, btnSecondary, fieldLabelCls, textareaCls } from '../../lib/styles';

interface Props {
  orderId: string;
  currentExecutor: string;
  onClose: () => void;
  onDone: () => void;
}

export function ChangeExecutorModal({ orderId, currentExecutor, onClose, onDone }: Props) {
  const [executor, setExecutor] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: accounts } = useAccounts('executor');
  const change = useChangeExecutor(orderId);

  // Активные исполнители, кроме текущего.
  const options = useMemo(
    () =>
      (accounts ?? [])
        .filter((a) => a.isActive && a.code !== currentExecutor)
        .map((a) => ({
          value: a.code,
          label: a.displayName ? `${a.displayName} · ${a.code}` : a.code,
        })),
    [accounts, currentExecutor],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!executor) {
      setError('Выберите нового исполнителя');
      return;
    }
    if (!reason.trim()) {
      setError('Укажите причину смены');
      return;
    }
    try {
      await change.mutateAsync({ executor, reason: reason.trim() });
      onDone();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal title={`Сменить исполнителя · ${orderId}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-text2">
          Текущий исполнитель:{' '}
          <span className="font-mono font-medium text-text">{currentExecutor}</span>. Обе ноги
          исполнителя перецепятся на нового контрагента; профит и суммы не изменятся. Смена
          заблокирована, если нога исполнителя уже подтверждена.
        </p>
        <label className="block">
          <span className={fieldLabelCls}>Новый исполнитель</span>
          <Combobox
            value={executor}
            onChange={setExecutor}
            options={options}
            placeholder="— выберите исполнителя —"
            emptyText="Нет доступных исполнителей"
          />
        </label>
        <label className="block">
          <span className={fieldLabelCls}>Причина смены</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className={textareaCls}
            placeholder="Например: исполнитель не выходит на связь"
          />
        </label>
        {error && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Отмена
          </button>
          <button type="submit" disabled={change.isPending} className={btnPrimary}>
            {change.isPending ? 'Сохранение…' : 'Сменить исполнителя'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
